import { useEffect, useState } from 'react';

import { useShowCampaignFinalizationStatus } from '@givebacks/api/store';
import { AxiosError, isAxiosError } from 'core';
import {
  Alert,
  ButtonMantine,
  Card,
  Group,
  List,
  Modal,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';
import { PublishSuccess } from 'ui/Icons';

import { useShowCurrentCampaign } from '../../../../../../api/new/campaigns/get';
import { useFinalizeCampaign } from '../../../../../../api/new/campaigns/update';
import { useCampaignInfoByProvider } from '../../../../../../api/new/campaigns/utils/campaignUtils';

type Props = {
  opened: boolean;
  onClose: () => void;
};

const FinalizeCampaignModalSuccess = ({ onClose }: { onClose: () => void }) => {
  return (
    <>
      <Stack gap="xs">
        <Title order={4}>
          Charging pledged donations is in progress. Return to your donation history table to review
          final charges.
        </Title>
        <PublishSuccess />
      </Stack>
      <Group justify="flex-end">
        <ButtonMantine w={160} onClick={onClose}>
          Close
        </ButtonMantine>
      </Group>
    </>
  );
};

const FinalizeAlert = ({
  title,
  message,
  cta,
  onCtaClick,
  isError,
}: {
  title: string;
  message: string;
  cta: string;
  onCtaClick: () => void;
  isError: boolean;
}) => {
  return (
    <Alert.Collapse
      type="error"
      title={
        <Group w="100%" gap="xs" wrap="nowrap" justify="space-between">
          <Text fz="sm" fw={500}>
            {title}
          </Text>
          <ButtonMantine
            variant="outline"
            color="cerise"
            size="xs"
            miw="fit-content"
            onClick={onCtaClick}
          >
            {cta}
          </ButtonMantine>
        </Group>
      }
      in={isError}
      styles={{ label: { width: '100%' } }}
    >
      {message}
    </Alert.Collapse>
  );
};

const FinalizeCampaignModalContent = ({
  onClose,
  setSuccess,
}: {
  onClose: () => void;
  setSuccess: (value: boolean) => void;
}) => {
  const { data: campaign } = useShowCurrentCampaign();
  const { causeId, campaignId } = useCampaignInfoByProvider();
  const { error, isLoading } = useShowCampaignFinalizationStatus({
    causeId,
    campaignId,
  });

  const { mutate: finalizeCampaign, isPending: isFinalizeLoading } = useFinalizeCampaign();

  const handleFinalize = () => {
    if (!campaignId) return;
    finalizeCampaign(campaignId, {
      onSuccess: () => {
        setSuccess(true);
      },
      onError: (e) => {
        const error = e as Error | AxiosError;
        if (isAxiosError(error)) {
          notifications.show({
            message: error?.response?.data.error,
            variant: 'error',
          });
        }
      },
    });
  };

  if (campaign?.__type !== 'athon') return null;
  return (
    <Stack gap={'lg'}>
      <Title order={4}>
        {`Once you finalize this campaign, your pledge donors will be charged per-${campaign?.units_of_activity_singular} completed.`}
      </Title>
      <Stack gap="xs">
        <FinalizeAlert
          title={isAxiosError(error) ? error?.response?.data?.error : 'Something went wrong'}
          message={`You can log ${campaign.participant_options.convention_singular.toLowerCase()} activity in Participants &
          Prizes.`}
          cta="Learn How to Log Activity"
          onCtaClick={() =>
            window.open(
              'https://support.givebacks.com/hc/en-us/articles/21360351590423-How-do-admins-add-activity-to-an-A-thon',
              '_blank'
            )
          }
          isError={
            isAxiosError(error) ? error?.response?.data?.attributes.includes('activity') : false
          }
        />
        <FinalizeAlert
          title="Write-in donations or pledges will not update participant and prize reports until
                you edit the credit designation"
          message="We recommend updating all write-ins prior to finalization."
          cta="Learn How to Edit Write-ins"
          onCtaClick={() =>
            window.open(
              'https://support.givebacks.com/hc/en-us/articles/360046045454-How-to-credit-a-write-in-donation',
              '_blank'
            )
          }
          isError={
            isAxiosError(error) ? error?.response?.data?.attributes.includes('write_in') : false
          }
        />
        <Card radius="sm" bg="gray.0" style={{ flexGrow: 1 }}>
          <Stack gap="xs">
            <Text fs="italic" fw={500} c="cobalt">
              What happens after my campaign has been finalized?
            </Text>
            <List type="ordered" size="sm" c="gray" spacing="xs">
              <List.Item>Admins and/or participants cannot log any new activity</List.Item>
              <List.Item>Admins cannot edit pledge amounts</List.Item>
              <List.Item>Admins cannot edit credit designations</List.Item>
              <List.Item>
                Final donation amounts will be charged based on pledges and logged activity
              </List.Item>
            </List>
          </Stack>
        </Card>
      </Stack>

      <Group justify="flex-end">
        <ButtonMantine size="md" loading={isFinalizeLoading} variant="subtle" onClick={onClose}>
          Cancel
        </ButtonMantine>
        <ButtonMantine size="md" loading={isLoading || isFinalizeLoading} onClick={handleFinalize}>
          Finalize Campaign
        </ButtonMantine>
      </Group>
    </Stack>
  );
};
const FinalizeCampaignWrapper = ({
  onClose,
  success,
  setSuccess,
}: {
  onClose: () => void;
  success: boolean;
  setSuccess: (value: boolean) => void;
}) => {
  useEffect(() => {
    return () => {
      setSuccess(false);
    };
  }, []);

  return (
    <Stack gap={'lg'}>
      {success ? (
        <FinalizeCampaignModalSuccess onClose={onClose} />
      ) : (
        <FinalizeCampaignModalContent onClose={onClose} setSuccess={setSuccess} />
      )}
    </Stack>
  );
};
const FinalizeCampaignModal = ({ opened, onClose }: Props) => {
  const [success, setSuccess] = useState(false);

  return (
    <Modal
      title={
        success
          ? 'Congrats, your campaign has been finalized!'
          : 'Would you like to finalize this campaign?'
      }
      size="lg"
      opened={opened}
      onClose={onClose}
    >
      <FinalizeCampaignWrapper onClose={onClose} success={success} setSuccess={setSuccess} />
    </Modal>
  );
};

export { FinalizeCampaignModal };
